import type {
  AddStaffFormData,
  AddStaffValidationRequirements,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";
import { isPaymentMethodValueValidOrEmpty } from "../shared/staff.paymentMethod.logic";
import { getEmploymentFieldVisibility } from "../shared/staffEmploymentFieldVisibility.logic";

const PHONE_WITH_COUNTRY_CODE_REGEX = /^\+?[1-9]\d{7,14}$/;
const PERSONAL_IDENTITY_NUMBER_REGEX = /^(\d{12}|\d{8}-\d{4})$/;
const MONTHLY_SALARY_TYPE = "Monthly";
const SIMFLEXITY_PAY_PAYMENT_TYPE = "SimflexityPay";

const isPositiveNumber = (value?: string): boolean => {
  if (!value || value.trim().length === 0) return false;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
};

const isPositiveInteger = (value?: string): boolean => {
  if (!value || value.trim().length === 0) return false;
  if (!/^\d+$/.test(value.trim())) return false;
  const parsedValue = Number(value);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0;
};

const hasNonEmptyValue = (value?: string): boolean => {
  return Boolean(value && value.trim().length > 0);
};

const hasValidPersonalIdentityNumber = (
  personalIdentityNumber: string,
): boolean => {
  const normalizedValue = personalIdentityNumber.trim();
  if (normalizedValue.length === 0) return true;
  return PERSONAL_IDENTITY_NUMBER_REGEX.test(normalizedValue);
};

const normalizeEmploymentType = (
  employmentType: AddStaffFormData["employmentType"],
): string => {
  return (employmentType ?? "").trim().toLowerCase();
};

const hasInvalidDateRange = (formData: AddStaffFormData): boolean => {
  if (!formData.startDate || !formData.endDate) return false;
  return !formData.endDate.isAfter(formData.startDate);
};

const isSimflexityEmployee = (formData: AddStaffFormData): boolean => {
  return (
    normalizeEmploymentType(formData.employmentType) ===
    EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE.toLowerCase()
  );
};

const hasValidWorkPercentageValue = (workPercentage?: string): boolean => {
  if (!workPercentage || workPercentage.trim().length === 0) return false;
  const parsedWorkPercentage = Number(workPercentage);
  return (
    Number.isFinite(parsedWorkPercentage) &&
    parsedWorkPercentage >= 1 &&
    parsedWorkPercentage <= 100
  );
};

/**
 * Validates phone number format for add staff flow.
 * Allowed: country code + subscriber digits, optional "+", no leading zero, 8-15 digits.
 * Example: 46723246798 or +46723246798
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  const normalizedPhoneNumber = phoneNumber.trim();
  return PHONE_WITH_COUNTRY_CODE_REGEX.test(normalizedPhoneNumber);
};

/**
 * Derives which fields are required based on selected employment type.
 */
export const getAddStaffValidationRequirements = (
  formData: AddStaffFormData,
): AddStaffValidationRequirements => {
  const employmentType = normalizeEmploymentType(formData.employmentType);
  const isConsultantEmployee =
    employmentType === EMPLOYMENT_TYPE_CONSULTANT.toLowerCase();
  const isTraineeEmployee =
    employmentType === EMPLOYMENT_TYPE_TRAINEE.toLowerCase();
  const isSimflexityEmployee =
    employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE.toLowerCase();
  const isProjectEmployee =
    employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE.toLowerCase();

  const isEmployeeNumberRequired =
    !isConsultantEmployee && !isTraineeEmployee && !isSimflexityEmployee;
  const visibility = getEmploymentFieldVisibility(formData.employmentType);

  return {
    isEmployeeNumberRequired,
    isEmployeeCategoryRequired: visibility.showEmployeeCategory,
    isSalaryModelRequired: visibility.showSalaryType,
    isSalaryAmountRequired: visibility.showSalary,
    isWorkPercentageRequired:
      visibility.showSalaryType && formData.salaryType === MONTHLY_SALARY_TYPE,
    isTaxTableRequired: visibility.showTaxTable,
    isPaymentTypeRequired: visibility.showPaymentType,
    isEndDateRequired: isProjectEmployee || isTraineeEmployee,
  };
};

const hasRequiredPersonalValues = (formData: AddStaffFormData): boolean => {
  if (!hasNonEmptyValue(formData.firstName)) return false;
  if (!hasNonEmptyValue(formData.lastName)) return false;
  if (!hasNonEmptyValue(formData.role)) return false;
  if (!hasValidPersonalIdentityNumber(formData.personalIdentityNumber))
    return false;
  if (formData.teamIds.length === 0) return false;
  return true;
};

/**
 * Company policy rules:
 * - SimflexityEmployee cannot use Monthly salary model.
 * - SimflexityEmployee must use SimflexityPay.
 * - Other employment types can only use SimflexityPay if policy allows it.
 */
const hasValidCompanyPolicyFields = (
  formData: AddStaffFormData,
  canSelectSimflexityPay: boolean,
): boolean => {
  const isSimflexity = isSimflexityEmployee(formData);

  if (isSimflexity && formData.salaryType === MONTHLY_SALARY_TYPE) return false;
  if (isSimflexity && formData.paymentType !== SIMFLEXITY_PAY_PAYMENT_TYPE) {
    return false;
  }
  if (!isSimflexity && formData.paymentType === SIMFLEXITY_PAY_PAYMENT_TYPE) {
    return canSelectSimflexityPay;
  }

  return true;
};

const hasRequiredEmploymentType = (formData: AddStaffFormData): boolean => {
  return hasNonEmptyValue(formData.employmentType);
};

const hasRequiredSalaryFields = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
  hasValidSalaryAmount: boolean,
  hasValidWorkPercentage: boolean,
): boolean => {
  if (
    requirements.isSalaryModelRequired &&
    !hasNonEmptyValue(formData.salaryType)
  ) {
    return false;
  }

  if (requirements.isSalaryAmountRequired && !hasValidSalaryAmount)
    return false;

  if (!hasValidWorkPercentage) return false;

  return true;
};

const hasRequiredCompensationFields = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
  hasValidPaymentMethod: boolean,
): boolean => {
  if (requirements.isTaxTableRequired && !hasNonEmptyValue(formData.taxTable))
    return false;

  if (!hasValidPaymentMethod) return false;

  if (
    requirements.isPaymentTypeRequired &&
    !hasNonEmptyValue(formData.paymentType)
  ) {
    return false;
  }

  return true;
};

const hasRequiredClassificationFields = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
  hasValidEmployeeNumber: boolean,
): boolean => {
  if (
    requirements.isEmployeeCategoryRequired &&
    !hasNonEmptyValue(formData.employeeCategory)
  ) {
    return false;
  }

  if (!hasValidEmployeeNumber) return false;

  return true;
};

const hasRequiredDateFields = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
): boolean => {
  if (!formData.startDate) return false;

  if (requirements.isEndDateRequired && !formData.endDate) return false;

  if (formData.endDate && hasInvalidDateRange(formData)) return false;

  return true;
};

const hasRequiredEmploymentValues = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
  hasValidSalaryAmount: boolean,
  hasValidEmployeeNumber: boolean,
  hasValidPaymentMethod: boolean,
  hasValidWorkPercentage: boolean,
  canSelectSimflexityPay: boolean,
): boolean => {
  const validationChecks = [
    hasRequiredEmploymentType(formData),
    hasRequiredSalaryFields(
      formData,
      requirements,
      hasValidSalaryAmount,
      hasValidWorkPercentage,
    ),
    hasRequiredCompensationFields(
      formData,
      requirements,
      hasValidPaymentMethod,
    ),
    hasRequiredClassificationFields(
      formData,
      requirements,
      hasValidEmployeeNumber,
    ),
    hasRequiredDateFields(formData, requirements),
    hasValidCompanyPolicyFields(formData, canSelectSimflexityPay),
  ];

  return validationChecks.every((isValid) => isValid);
};

/**
 * Produces UI-friendly validation state for add staff form.
 * `canSelectSimflexityPay` reflects company subscription/settings policy.
 */
export const getAddStaffValidationState = (
  formData: AddStaffFormData,
  requirements: AddStaffValidationRequirements,
  canSelectSimflexityPay = false,
): AddStaffValidationState => {
  const hasValidSalaryAmount = isPositiveNumber(formData.salaryAmount);
  const hasValidEmployeeNumber = requirements.isEmployeeNumberRequired
    ? isPositiveInteger(formData.employeeNumber)
    : true;
  const hasValidPaymentMethod = isPaymentMethodValueValidOrEmpty(
    formData.paymentMethodType,
    formData.paymentMethodValue,
  );
  const hasValidWorkPercentage = requirements.isWorkPercentageRequired
    ? hasValidWorkPercentageValue(formData.workPercentage)
    : true;

  const hasRequiredEmployment = hasRequiredEmploymentValues(
    formData,
    requirements,
    hasValidSalaryAmount,
    hasValidEmployeeNumber,
    hasValidPaymentMethod,
    hasValidWorkPercentage,
    canSelectSimflexityPay,
  );

  return {
    hasValidSalaryAmount,
    hasValidEmployeeNumber,
    hasValidPaymentMethod,
    hasValidWorkPercentage,
    hasRequiredPersonalValues: hasRequiredPersonalValues(formData),
    hasRequiredEmploymentValues: hasRequiredEmployment,
  };
};
